import React, { useCallback, useEffect, useState } from "react";
import { Settings as SettingsIcon, ShieldAlert, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { efAdminSettings, efAdminUpdateSettings } from "../../lib/api";
import { AdminIdleLockSettings } from "./AdminIdleLockSettings";

/**
 * AdminSettingsPanel — top-level admin settings.
 *
 * Currently surfaces the Archive Status Notice ("Beta / Under Construction")
 * toggle prominently so editors can flip it without navigating into a
 * sub-tab. Setting is persisted on the server in `ef_settings`.
 *
 * Setting keys (server-side):
 *   - archive_status_notice_enabled : bool
 *   - archive_status_notice_mode    : "active_development" | "beta_testing" | "maintenance" | "emergency"
 */

const MODES = [
  { key: "active_development", label: "Active Development",
    desc: "Default — investigations and dossiers are being added regularly." },
  { key: "beta_testing", label: "Beta Testing",
    desc: "We're testing new features; bumps may occur." },
  { key: "maintenance", label: "Maintenance",
    desc: "Scheduled or active maintenance window." },
  { key: "emergency", label: "Emergency Notice",
    desc: "Urgent operational notice for visitors." },
];

export const AdminSettingsPanel = ({ token, userToken = null }) => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const s = await efAdminSettings(token, userToken);
      setSettings(s);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load settings");
    } finally {
      setLoading(false);
    }
  }, [token, userToken]);

  useEffect(() => { load(); }, [load]);

  const patch = async (body, successMsg) => {
    setBusy(true);
    try {
      const s = await efAdminUpdateSettings(token, body, userToken);
      setSettings(s);
      if (successMsg) toast.success(successMsg);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="admin-settings-panel">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
        <div>
          <div className="font-mono text-[10px] tracking-[0.3em] text-[#D92D20] mb-2 flex items-center gap-2">
            <SettingsIcon size={13} /> // SETTINGS
          </div>
          <h1 className="font-display text-3xl sm:text-4xl text-[#E4DFD3]">
            Settings
          </h1>
          <p className="text-[#A39E93] mt-1 text-sm max-w-2xl">
            Public-facing notices, editorial toggles, and platform-wide flags. All
            changes are audit-logged.
          </p>
        </div>
      </div>

      {loading || !settings ? (
        <div className="font-mono text-xs text-[#757065]">// LOADING…</div>
      ) : (
        <BetaNoticeCard
          enabled={!!settings.archive_status_notice_enabled}
          mode={settings.archive_status_notice_mode || "active_development"}
          busy={busy}
          onToggle={() => patch(
            { archive_status_notice_enabled: !settings.archive_status_notice_enabled },
            `Archive Status Notice → ${!settings.archive_status_notice_enabled ? "ON" : "OFF"}`,
          )}
          onModeChange={(m) => patch(
            { archive_status_notice_mode: m },
            `Notice mode → ${m}`,
          )}
        />
      )}

      <div className="mt-4">
        <AdminIdleLockSettings token={token} userToken={userToken} />
      </div>
    </div>
  );
};

const BetaNoticeCard = ({ enabled, mode, busy, onToggle, onModeChange }) => (
  <div
    className="border border-[#2D2A26] bg-[#0a0907] mb-4"
    data-testid="admin-beta-notice-card"
  >
    <div className="flex items-center gap-2 border-b border-[#2D2A26] px-4 py-2">
      <ShieldAlert size={14} className="text-[#E0A526]" />
      <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#E0A526]">
        // ARCHIVE STATUS NOTICE · BETA / UNDER CONSTRUCTION
      </span>
    </div>
    <div className="p-4 space-y-3">
      <p className="text-[12px] text-[#CBB892] leading-relaxed">
        When ON, every public visitor sees a full-screen entry notice before
        entering the site. Setting persists server-side. Visitors dismiss for
        7 days via localStorage. Admin / legal / editorial-standards pages are
        never blocked.
      </p>

      <div className="flex items-center gap-3 flex-wrap">
        <button
          type="button"
          onClick={onToggle}
          disabled={busy}
          data-testid="admin-beta-notice-toggle"
          className={
            "inline-flex items-center gap-2 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.3em] border " +
            (enabled
              ? "bg-[#0c1a0e] border-[#5DD4B3] text-[#5DD4B3]"
              : "bg-[#1a0a0a] border-[#D92D20] text-[#D92D20]")
          }
        >
          {busy ? <Loader2 size={12} className="animate-spin" /> : (enabled ? <CheckCircle2 size={12} /> : null)}
          {enabled ? "ON" : "OFF"}
        </button>
        <span className="font-mono text-[10px] text-[#757065]">
          {enabled
            ? "public visitors will see the entry notice"
            : "no entry notice — public visitors enter directly"}
        </span>
      </div>

      <div>
        <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#A39E93] mb-2">
          mode
        </div>
        <div className="grid sm:grid-cols-2 gap-2">
          {MODES.map((m) => {
            const active = mode === m.key;
            return (
              <button
                key={m.key}
                type="button"
                onClick={() => onModeChange(m.key)}
                disabled={busy}
                data-testid={`admin-beta-mode-${m.key}`}
                className={
                  "text-left border p-2.5 transition " +
                  (active
                    ? "border-[#E0A526] bg-[#1a1408]"
                    : "border-[#2D2A26] hover:border-[#454038]")
                }
              >
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#E4DFD3]">
                  {m.label}
                </div>
                <div className="text-[11px] text-[#A39E93] mt-0.5">{m.desc}</div>
              </button>
            );
          })}
        </div>
      </div>

      <div className="font-mono text-[9px] text-[#5a544a] uppercase tracking-[0.25em]">
        // setting key · archive_status_notice_enabled · archive_status_notice_mode
      </div>
    </div>
  </div>
);

export default AdminSettingsPanel;
